import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import ModuleHeader from "@/components/voff/ModuleHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SOURCES = {
  "International Bodies": ["IMO (MSC, MEPC, FAL)", "IMCA (Safety Flashes)", "ILO (MLC amendments)"],
  "Flag State": ["RMI / IRI Notices", "Marshall Islands Maritime Admin"],
  "Classification": ["ABS Notices to Mariners", "ABS Class Notes"],
  "Industry": ["BIMCO Notices", "Nautical Institute", "OCIMF Guidance"],
  "Regional": ["ANPG (Angola)", "GNPOC (Guyana)", "NIMASA (Nigeria)"],
};

const FEED = [
  { ref: "IMO MSC.1/Circ.1665", date: "2026-04-10", source: "IMO", relevance: "HIGH", title: "STCW Training for SES/High-Speed Craft Operators — Revised Guidance",
    summary: "Revised guidance on STCW training requirements for officers operating high-speed craft including SES.",
    impact: "Directly affects crew certification for AIRCAT 35 SES operations. Review crew training matrix against new guidance before next vessel delivery.",
    owner: "Kyle Pemberton (COO)", due: "30 days", action: true },
  { ref: "IMCA SF 04/26", date: "2026-04-08", source: "IMCA", relevance: "HIGH", title: "DP2 Reference System Failure — Lessons from Incident",
    summary: "Recent loss of position event in West Africa attributed to single-point failure in DGPS reference; all DP2 operators advised to review redundancy.",
    impact: "Review K-Pos DP2 BITE test procedure on AIRCAT 35 fleet; verify reference redundancy.",
    owner: "Eduard Ercegovic", due: "14 days", action: true },
  { ref: "ABS NM-2026-08", date: "2026-04-05", source: "ABS", relevance: "MEDIUM", title: "HSC Code Amendments — Lift Fan Bearing Inspection Intervals",
    summary: "Revised inspection intervals for lift fan assemblies on Surface Effect Ships under HSC Code Ch.10.",
    impact: "Update PMS schedules to align with new ABS interval; current 250hr interval likely needs reduction to 200hr.",
    owner: "PMS Team", due: "60 days", action: true },
  { ref: "RMI MN-2-011-78", date: "2026-04-01", source: "RMI", relevance: "MEDIUM", title: "Cyber Security Reporting Obligations Update",
    summary: "RMI now requires cyber incident reporting within 72 hours; expanded scope to include navigation systems.",
    impact: "Update Corp008 Cyber Policy to reflect 72-hour reporting timeline; brief Master cohort.", owner: "CISO", due: "45 days", action: true },
  { ref: "ANPG-2026-CN-014", date: "2026-03-28", source: "ANPG", relevance: "HIGH", title: "Angola Local Content — CTV Crew Quotas Revised",
    summary: "ANPG raises Angolan national crew quota requirement from 30% to 50% for offshore support vessels by Q4 2026.",
    impact: "Affects Azule Energy operation; accelerate Angolan crew development pipeline.", owner: "Bo Jardine (CCO)", due: "Strategic", action: true },
  { ref: "OCIMF G-2026-03", date: "2026-03-22", source: "OCIMF", relevance: "LOW", title: "Updated MEG4 Mooring Equipment Guidelines",
    summary: "Minor amendments to MEG4 mooring guidelines.", impact: "Limited applicability to CTV operations; informational.",
    owner: "—", due: "—", action: false },
  { ref: "BIMCO-2026-04", date: "2026-03-15", source: "BIMCO", relevance: "MEDIUM", title: "New SUPPLYTIME 2026 Pro-Forma Released",
    summary: "Updated SUPPLYTIME charter party form addressing recent market practice.", impact: "Review for VOFF clause library; potential template upgrade for new charters.",
    owner: "Bo Jardine (CCO)", due: "Q2 2026", action: true },
];

const ACTIONS = FEED.filter(f => f.action).map((f, i) => ({ ...f, status: ["OPEN","IN PROGRESS","OPEN","OPEN","IN PROGRESS","OPEN","OPEN"][i] || "OPEN" }));

const relevanceTone = (r: string) => r === "HIGH" ? "bg-[#C83228]" : r === "MEDIUM" ? "bg-[#E8A020]" : "bg-[#6B8A9A]";

const RegulatoryRadar = () => {
  const [scanning, setScanning] = useState(false);
  const runScan = () => { setScanning(true); setTimeout(() => { setScanning(false); toast.success("Scan complete — 7 notices reviewed"); }, 1800); };

  return (
    <IntranetLayout>
      <ModuleHeader title="Regulatory Radar" subtitle="IMO | IMCA | RMI | ABS | MLC — Regulatory Intelligence"
        stats={[{ label: "Unread", value: 4, tone: "alert" }, { label: "Action req'd", value: 6, tone: "alert" }, { label: "Last scan", value: "10 Apr" }]} />

      <div className="grid lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-3 border-[#D0DDE6]">
          <CardContent className="p-5">
            <h3 className="font-bold text-sm text-[#00283C] mb-3">Source Filters</h3>
            {Object.entries(SOURCES).map(([cat, items]) => (
              <div key={cat} className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-[#6B8A9A] mb-2">{cat}</div>
                {items.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-xs py-1"><Checkbox defaultChecked /><span>{s}</span></label>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="lg:col-span-6 space-y-4">
          <div className="flex gap-2">
            <Button onClick={runScan} disabled={scanning} className="bg-[#8CB43C] hover:bg-[#8CB43C]/90 text-white">
              <RefreshCw className={`w-4 h-4 mr-2 ${scanning ? "animate-spin" : ""}`} />Run Scan Now
            </Button>
            <Button onClick={() => toast.success("Action recorded: Mark all read")} variant="outline" size="sm">Mark all read</Button>
          </div>
          {FEED.map((n) => (
            <Card key={n.ref} className="border-[#D0DDE6]">
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="text-xs text-[#6B8A9A]">{n.ref} • {n.source}</div>
                    <div className="font-bold text-[#00283C] text-sm mt-0.5">{n.title}</div>
                  </div>
                  <Badge className={`${relevanceTone(n.relevance)} text-white text-[10px]`}>{n.relevance}</Badge>
                </div>
                <p className="text-xs text-[#0D1E2B] mb-2">{n.summary}</p>
                <div className="bg-[#F4F7FA] p-2 rounded text-xs">
                  <div className="font-semibold text-[#00283C]">VOFF Impact:</div>
                  <div className="text-[#0D1E2B]">{n.impact}</div>
                </div>
                {n.action && (
                  <div className="flex justify-between items-center mt-2 text-xs">
                    <div><span className="text-[#6B8A9A]">Owner:</span> {n.owner} • <span className="text-[#6B8A9A]">Due:</span> {n.due}</div>
                    <Button onClick={() => toast.success("Action recorded: Action")} size="sm" variant="ghost" className="h-7"><ExternalLink className="w-3 h-3" /></Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="lg:col-span-3 border-[#D0DDE6] h-fit">
          <CardContent className="p-5">
            <h3 className="font-bold text-sm text-[#00283C] mb-3">Action Register</h3>
            <div className="space-y-2 mb-4">
              {ACTIONS.slice(0, 6).map((a) => (
                <div key={a.ref} className="text-xs border-b border-[#D0DDE6]/50 pb-2">
                  <div className="font-semibold text-[#00283C]">{a.ref}</div>
                  <div className="flex justify-between text-[#6B8A9A]"><span>{a.due}</span><Badge variant="outline" className="text-[9px] h-4">{a.status}</Badge></div>
                </div>
              ))}
            </div>
            <div className="bg-[#F4F7FA] p-3 rounded text-xs space-y-1">
              <div className="flex justify-between"><span>Open:</span><span className="font-bold">6</span></div>
              <div className="flex justify-between"><span>Overdue:</span><span className="font-bold text-[#C83228]">1</span></div>
              <div className="flex justify-between"><span>Closed Q2:</span><span className="font-bold text-[#2CA048]">12</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </IntranetLayout>
  );
};
export default RegulatoryRadar;
